/* import __COLOCATED_TEMPLATE__ from './line-settings-side-menu.hbs'; */
/* RESPONSIBLE TEAM: team-fin-voice */
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import { PhoneNumberType } from 'embercom/models/settings/calling';

interface Arguments {
  isOpen: boolean;
  onClose: () => void;
  openHelpArticle: () => void;
  phoneNumberId?: string;
  numberConfiguration?: FinVoiceNumberConfiguration;
}

export default class LineSettingsSideMenu extends Component<Arguments> {
  @service declare notificationsService: $TSFixMe;
  @service declare intl: IntlService;
  @service declare finVoiceConfigService: FinVoiceConfigService;

  @tracked showIpAddressModal = false;
  @tracked showDeleteModal = false;
  @tracked lineName = this.phoneNumberRecord?.name || '';

  get numberConfiguration() {
    return this.args.numberConfiguration;
  }

  get phoneNumbers() {
    return this.finVoiceConfigService.phoneNumbers;
  }

  get phoneNumberRecord() {
    if (!this.args.phoneNumberId) {
      return undefined;
    }
    return this.phoneNumbers[this.args.phoneNumberId] || undefined;
  }

  get isSipLine() {
    return this.phoneNumberRecord?.phoneType === PhoneNumberType.Sip;
  }

  get ipRange() {
    return this.phoneNumberRecord?.ipRangeArray || [];
  }

  @action
  setLineName(value: string) {
    this.lineName = value;
  }

  @action
  openIpAddressModal() {
    this.showIpAddressModal = true;
  }

  @action
  closeIpAddressModal() {
    this.showIpAddressModal = false;
  }

  @action
  async addIpAddress(ipAddress: string) {
    if (!this.args.phoneNumberId) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.phone-number-not-found'),
      );
      return;
    }
    await this.finVoiceConfigService.addIpAddress(this.args.phoneNumberId, ipAddress);
    await this.finVoiceConfigService.loadAllData();
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.voice-playground.line-settings.ip-address-added'),
    );
  }

  @action
  async removeIpAddress(ipAddress: string) {
    if (!this.args.phoneNumberId) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.phone-number-not-found'),
      );
      return;
    }
    await this.finVoiceConfigService.removeIpAddress(this.args.phoneNumberId, ipAddress);
    await this.finVoiceConfigService.loadAllData();
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.voice-playground.line-settings.ip-address-removed'),
    );
  }

  @action
  handleModalClose() {
    this.args.onClose();
  }

  @action
  async deleteLine() {
    try {
      await this.phoneNumberRecord?.destroyRecord();
      await this.finVoiceConfigService.loadAllData();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.voice-playground.line-settings.delete.success', {
          name: this.phoneNumberRecord?.name,
        }),
      );
      this.args.onClose();
    } catch (e) {
      this.notificationsService.notifyResponseError(e);
      return;
    }
  }

  @action
  async saveLineSettings() {
    try {
      if (!this.phoneNumberRecord) {
        return;
      }
      this.phoneNumberRecord.name = this.lineName;
      await this.phoneNumberRecord.save();
      await this.finVoiceConfigService.loadAllData();
      this.notificationsService.notifyConfirmation(
        this.intl.t('ai-agent.voice-playground.line-settings.save-success'),
      );
    } catch (e) {
      this.notificationsService.notifyResponseError(e);
      return;
    }
  }

  @action
  async copyNumber() {
    if (!this.phoneNumberRecord) {
      return;
    }

    await navigator.clipboard.writeText(this.phoneNumberRecord.phoneNumber);
    this.notificationsService.notifyConfirmation(
      this.intl.t('ai-agent.voice-playground.line-settings-copied'),
    );
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::Voice::LineSettingsSideMenu': typeof LineSettingsSideMenu;
  }
}
